import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Github, Linkedin, Twitter, Mail, ArrowUp, ArrowUpRight } from 'lucide-react';

export const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { name: 'Who Am I?', id: 'about' },
    { name: 'What Am I Capable Of?', id: 'capabilities' },
    { name: 'My Working Model', id: 'process' },
    { name: 'Public Reviews', id: 'testimonials' },
    { name: 'How To Contact Me', id: 'contact' },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1] as any // Premium cubic bezier easeOut
      }
    }
  };

  return (
    <footer id="footer" className="relative bg-background text-foreground border-t border-border py-16 md:py-24 overflow-hidden transition-colors duration-300">
      {/* Subtle radial glow background effect matching design specs */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-primary/[0.03] via-transparent to-transparent pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-6 sm:px-12 relative z-10">
        
        {/* Upper Footer section with Framer Motion entry viewport trigger */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-border"
        >
          
          {/* Brand Presentation Column */}
          <motion.div variants={itemVariants} className="md:col-span-8 space-y-6">
            <div className="flex items-center gap-4">
              <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full p-0.5 bg-[#261006] border border-[#caa062]/80 shadow-[0_8px_24px_rgba(0,0,0,0.9),inset_0_1.5px_2px_rgba(255,255,255,0.25)] shrink-0">
                <img 
                  src="/brand_logo.png?v=4" 
                  alt="The Brown Pelican Man Emblem" 
                  referrerPolicy="no-referrer"
                  className="w-full h-full rounded-full object-contain drop-shadow-md" 
                />
              </div>
              <div className="flex flex-col items-start">
                <span className="text-lg sm:text-xl font-display tracking-widest uppercase font-semibold leading-tight text-foreground">
                  The Brown Pelican Man
                </span>
                <span className="text-[9px] sm:text-[10px] font-space text-[#d8aa68] tracking-widest uppercase mt-1.5 font-mono font-bold">
                  Dive Deep · Build Big
                </span>
              </div>
            </div>
            <p className="text-sm font-light text-muted-foreground leading-relaxed max-w-sm">
              Venture Builder, Product Architect &amp; Systems Engineer. I build custom web &amp; mobile apps, high-power admin dashboards, authenticated login portals, and enterprise-grade security architectures that drive measurable business value.
            </p>
          </motion.div>

          {/* Quick Links Column */}
          <motion.div variants={itemVariants} className="md:col-span-4 space-y-5 pt-2 md:pt-0">
            <h4 className="text-xs font-mono font-bold uppercase tracking-widest text-[#d8aa68]">
              Directory
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => {
                      const el = document.getElementById(link.id);
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-xs font-space uppercase tracking-widest text-gray-400 hover:text-[#ffe0a3] transition-colors flex items-center gap-1.5 group"
                  >
                    <span>{link.name}</span>
                    <ArrowUpRight size={11} className="opacity-0 -translate-y-0.5 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>



        </motion.div>

        {/* Lower Legalese / Copyright Section with fade-in */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="flex flex-col md:flex-row justify-between items-center pt-10 gap-6"
        >
          
          {/* Legalese links and Copyright */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 text-center sm:text-left">
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">
              © {new Date().getFullYear()} NAVEEN PALADUGU. ALL RIGHTS RESERVED.
            </span>
            <div className="flex gap-6 text-[10px] font-mono uppercase tracking-widest">
              <Link 
                to="/privacy" 
                className="text-gray-500 hover:text-[#ffe0a3] transition-colors underline decoration-dotted underline-offset-4"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-gray-500 hover:text-[#ffe0a3] transition-colors underline decoration-dotted underline-offset-4"
              >
                Terms of Service
              </Link>
            </div>
          </div>

          {/* Back to Top Floating-style Button */}
          <motion.button
            onClick={scrollToTop}
            whileHover={{ y: -3, scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-border bg-card/40 hover:bg-[#381b0e]/30 hover:border-[#d8aa68]/40 transition-all font-mono text-[10px] uppercase tracking-widest text-gray-400 hover:text-[#ffe0a3] group"
          >
            <span>Back to Top</span>
            <ArrowUp size={12} className="group-hover:-translate-y-0.5 transition-transform" />
          </motion.button>

        </motion.div>

      </div>
    </footer>
  );
};
